import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { Menu, MenuItem, MenuItemProps, MenuTrigger, Separator } from 'react-aria-components';
import { AgButton, type AgButtonProps } from './AgButton';
import { AgPopover } from './AgPopover';
import { cn } from './utils';

export interface AgDropdownMenuProps {
  /** The trigger button content */
  children?: React.ReactNode;
  /** Menu items - use AgDropdownItem components */
  items: React.ReactNode;
  /** Show chevron icon in trigger */
  showChevron?: boolean;
  /** Icon to show in trigger */
  icon?: React.ReactNode;
  /** Trigger variant style */
  variant?: Pick<AgButtonProps, 'variant'>['variant'];
  /** Trigger size */
  size?: Pick<AgButtonProps, 'size'>['size'];
  /** Trigger shape */
  shape?: Pick<AgButtonProps, 'shape'>['shape'];
  /** Additional class for trigger button */
  className?: string;
  /** Additional class for menu popover */
  menuClassName?: string;
  /** Disable the dropdown */
  isDisabled?: boolean;
  /** Alignment of the menu */
  align?: 'start' | 'end';
  /** Remove from tab order (e.g. grid action cells) */
  excludeFromTabOrder?: boolean;
  /** Test ID for E2E tests */
  'data-testid'?: string;
}

export function AgDropdownMenu({
  children,
  items,
  showChevron = true,
  icon,
  variant = 'white',
  size = 'md',
  shape = 'rounded',
  className,
  menuClassName,
  isDisabled,
  align = 'start',
  excludeFromTabOrder,
  'data-testid': dataTestId,
}: AgDropdownMenuProps) {
  const isIconOnly = size?.toString().startsWith('icon-');

  return (
    <MenuTrigger>
      <AgButton
        variant={variant}
        size={size}
        shape={shape}
        className={cn('data-pressed:border-brand-focus', className)}
        isDisabled={isDisabled}
        excludeFromTabOrder={excludeFromTabOrder}
        data-testid={dataTestId}
      >
        {({ isPressed }) => (
          <>
            {icon && !isIconOnly && <span className="shrink-0">{icon}</span>}
            {isIconOnly ? icon : children}
            {showChevron &&
              !isIconOnly &&
              (isPressed ? (
                <RiArrowUpSLine className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
              ) : (
                <RiArrowDownSLine className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
              ))}
          </>
        )}
      </AgButton>
      <AgPopover
        placement={align === 'end' ? 'bottom end' : 'bottom start'}
        className={cn('min-w-55 p-1.5', menuClassName)}
      >
        <Menu className="outline-none max-h-[300px] overflow-auto">{items}</Menu>
      </AgPopover>
    </MenuTrigger>
  );
}

export interface AgDropdownItemProps extends Omit<MenuItemProps, 'children'> {
  children: React.ReactNode;
  /** Destructive/danger styling */
  destructive?: boolean;
  /** Icon to show before text */
  icon?: React.ReactNode;
}

export function AgDropdownItem({ children, className, destructive, icon, ...props }: AgDropdownItemProps) {
  return (
    <MenuItem
      className={cn(
        'relative flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer outline-none text-14',
        destructive
          ? 'text-red-text hover:bg-red-bg data-focused:bg-red-bg'
          : 'text-neutral-text hover:bg-[rgba(10,15,41,0.04)] data-focused:bg-[rgba(10,15,41,0.04)]',
        'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
        className
      )}
      {...props}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </MenuItem>
  );
}

export function AgDropdownSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <Separator className={cn('my-1 h-px bg-neutral-5', className)} {...props} />;
}
